import { useContext, useEffect, useState } from "react";
import { Layout } from "../../../../containers/layout/dashboard";
import { CreateInstitutionWrapper } from "./styled";
import { Context } from "../../../../context";
import { useNavigate } from "react-router-dom";
import { Box, CircularProgress, Grid2, SelectChangeEvent, Typography } from "@mui/material";
import { BaseFieldSet } from "../../../../components/formfields/fieldset/styled";
import { BaseLabel } from "../../../../components/formfields/label/styled";
import { BaseInput } from "../../../../components/formfields/input/styled";
import { BaseSelect } from "../../../../components/formfields/select/styled";
import { BaseOption } from "../../../../components/formfields/option/styled";
import { BaseButton } from "../../../../components/button/styled";
// import { KeyboardArrowDown } from "@mui/icons-material";
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import { BaseAlertModal } from "../../../../components/modals/alert";
import { createInstitutionService } from "../../../../utils/apis/institution/createInstitution";
import Cookies from "universal-cookie";
import { retrieveNGAStatesService } from "../../../../config/state/states";
import { retrieveLGAsService } from "../../../../config/lga/lgas";
import { retrieveStatusesService } from "../../../../config/status/statuses";
import { retrieveAllCurrencyService } from "../../../../utils/apis/configuration/currency/retrieveAllCurrencies";
import { retrieveStateByIdService } from "../../../../config/state/stateById";
import { useQuery } from "@tanstack/react-query";
import { retrieveCountriesService } from "../../../../config/country/countries";
import { retrieveCountryByIdService } from "../../../../config/country/countryById";
import { retrieveOtherStatesService } from "../../../../config/other/states";
import { retrieveOtherCitiesService } from "../../../../config/other/cities";

export const CreateInstitution = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const navigate = useNavigate();
    const { setActiveNavItem } = useContext(Context);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [openAlertModal, setOpenAlertModal] = useState(false);
    const [formDetails, setFormDetails] = useState<Record<string, any>>({
        name: "",
        phone: "",
        email: "",
        address: "",
        country: " ",
        state: " ",
        lga: " ",
        currency: " ",
        status: " ",
        logo: "",
        supportingCurrencies: []
    });

    useEffect(() => {
        setActiveNavItem("Institutions");
    }, [setActiveNavItem]);

    const { data: statuses } = useQuery({
        queryKey: ['statuses', TOKEN],
        queryFn: () => retrieveStatusesService(TOKEN),
        enabled: !!TOKEN,
    });

    const { data: currencies } = useQuery({
        queryKey: ['currencies', TOKEN],
        queryFn: () => retrieveAllCurrencyService(TOKEN),
        enabled: !!TOKEN,
    });

    const { data: countries } = useQuery({
        queryKey: ['countries', TOKEN],
        queryFn: () => retrieveCountriesService(TOKEN),
        enabled: !!TOKEN,
    });

    const { data: states } = useQuery({
        queryKey: ['states', TOKEN, formDetails.country],
        queryFn: async () => {
            const countryId = formDetails?.country?.trim() ? formDetails.country : null;
            if (!countryId) return;
            const country = await retrieveCountryByIdService(TOKEN, countryId);
            if (country.title !== "Nigeria" && country.title !== "Canada") return;
            let response;
            if (country.title === "Nigeria") {
                response = await retrieveNGAStatesService(TOKEN);
            } else {
                const res = await retrieveOtherStatesService({ country: country?.title });
                response = res?.data?.states
            }
            return response;
        },
        enabled: !!TOKEN,
    });

    const { data: LGAs } = useQuery({
        queryKey: ['LGAs', TOKEN, formDetails.state],
        queryFn: async () => {
            const countryId = formDetails?.country?.trim() ? formDetails.country : null;
            if (!countryId) return;
            const country = await retrieveCountryByIdService(TOKEN, countryId);
            if (country.title !== "Nigeria" && country.title !== "Canada") return;
            const stateId = formDetails.state?.trim() ? formDetails.state : "0";
            if (country.title === "Nigeria") {
                const stateInfo = await retrieveStateByIdService(TOKEN, stateId);
                return await retrieveLGAsService(stateInfo.title);
            } else {
                const res = await retrieveOtherStatesService({ country: country?.title });
                const stateInfo = res?.data?.states?.[stateId - 1];
                const cities = await retrieveOtherCitiesService({ country: country?.title, state: stateInfo?.name });
                return cities?.data;
            }
        },
        enabled: !!TOKEN,
    });

    const handleAlertModalPersist = () => {
        return setOpenAlertModal(true);
    };

    const handleNavigateToPrevious = () => {
        return navigate(-1);
    };

    const handleNavigateToDashboard = () => {
        return navigate("/dashboard");
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | SelectChangeEvent<unknown>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => {
            if (name === "supportingCurrencies") {
                const selectedValues = Array.isArray(value) ? value : [value];
                const filteredValues = selectedValues.filter(val => val !== "");
                return {
                    ...prev,
                    [name]: filteredValues,
                };
            } else if (name === "currency") {
                return {
                    ...prev,
                    supportingCurrencies: [],
                    [name]: value,
                };
            } else {
                return {
                    ...prev,
                    [name]: value,
                };
            }
        });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const target = e.target as HTMLInputElement;
        const { name, files } = target;
        if (files && files[0]) {
            setFormDetails((prev) => ({
                ...prev,
                [name]: files[0]
            }))
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        const formData = new FormData();
        const data = {
            name: formDetails.name,
            phone: formDetails.phone,
            email: formDetails.email,
            address: formDetails.address,
            country: formDetails.country,
            state: formDetails.state,
            lga: formDetails.lga,
            currency: formDetails.currency,
            status: formDetails.status,
            logo: formDetails.logo,
        };
        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value);
        });
        formData.append("supportingCurrencies", JSON.stringify(formDetails.supportingCurrencies));
        try {
            const response = await createInstitutionService(formData, TOKEN);
            if (response.status === "success") {
                setIsLoading(false);
                setOpenAlertModal(true);
            } else {
                setIsLoading(false);
                setError('Failed to create institution. Please check your credentials and try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to create institution. ${error.message}`);
            console.error('Failed to create institution:', error);
        }
    }

    return (
        <Layout
            id="create-institution"
        >
            <CreateInstitutionWrapper>
                <BaseAlertModal
                    title="Success"
                    message="Institution successfully created"
                    open={openAlertModal}
                    handleClose={handleAlertModalPersist}
                    callToAction="Continue"
                    handleCallToAction={handleNavigateToDashboard}
                />
                <Box
                    component={"div"}
                    className="title-box"
                >
                    <Typography
                        variant="h1"
                        fontFamily={"Inter"}
                        fontWeight={700}
                        fontSize={24}
                        lineHeight={"normal"}
                        color={"#373737"}
                        marginBlockEnd={"calc(var(--basic-margin)/4)"}
                    >
                        Create Institution
                    </Typography>
                    <BaseButton
                        variant="outlined"
                        border="1px solid #D7D7D7"
                        colour="#373737"
                        bgcolor="#FFFFFF"
                        padding="calc(var(--basic-padding)/4) calc(var(--basic-padding)/2)"
                        fontSize={12}
                        fontWeight={800}
                        startIcon={<ArrowLeftIcon />}
                        onClick={handleNavigateToPrevious}
                    >
                        <Typography
                            variant={"button"}
                            fontFamily={"inherit"}
                            fontWeight={"inherit"}
                            fontSize={"inherit"}
                            lineHeight={"inherit"}
                            color={"inherit"}
                            textTransform={"inherit"}
                        >
                            Back
                        </Typography>
                    </BaseButton>
                </Box>
                <form
                    onSubmit={handleSubmit}
                >
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Name
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="name"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    placeholder="Enter name"
                                    value={formDetails.name}
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Phone number
                                </BaseLabel>
                                <BaseInput
                                    required
                                    type="phone"
                                    name="phone"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    placeholder="Enter phone number"
                                    value={formDetails.phone}
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Email address
                                </BaseLabel>
                                <BaseInput
                                    required
                                    type="email"
                                    name="email"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    placeholder="username@gmail.com"
                                    value={formDetails.email}
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Office address
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="address"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    placeholder="Enter address"
                                    value={formDetails.address}
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                    </Grid2>
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Country
                                </BaseLabel>
                                <BaseSelect
                                    required
                                    name="country"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    // IconComponent={<KeyboardArrowDown />}
                                    value={formDetails.country}
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                        colour="#666666"
                                        fontWeight={400}
                                    >
                                        Select Country
                                    </BaseOption>
                                    {countries?.map((country: Record<any, any>, index: string) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={country.id}
                                                colour="#666666"
                                                fontWeight={400}
                                            >
                                                {country.title}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    State/Province
                                </BaseLabel>
                                <BaseSelect
                                    required
                                    name="state"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    // IconComponent={<KeyboardArrowDown />}
                                    value={formDetails.state}
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                        colour="#666666"
                                        fontWeight={400}
                                    >
                                        Select state/province
                                    </BaseOption>
                                    {states?.map((state: Record<any, any>, index: string) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={String(state.id || index + 1)}
                                                colour="#666666"
                                                fontWeight={400}
                                            >
                                                {state.title || state.name}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    LGA/City
                                </BaseLabel>
                                <BaseSelect
                                    required
                                    name="lga"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    // IconComponent={<KeyboardArrowDown />}
                                    value={formDetails.lga}
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                        colour="#666666"
                                        fontWeight={400}
                                    >
                                        Select LGA/City
                                    </BaseOption>
                                    {LGAs?.map((lga: Record<any, any>, index: string) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={String(lga.id || index + 1)}
                                                colour="#666666"
                                                fontWeight={400}
                                            >
                                                {lga.title || lga}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Base Currency
                                </BaseLabel>
                                <BaseSelect
                                    required
                                    name="currency"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    // IconComponent={<KeyboardArrowDown />}
                                    value={formDetails.currency}
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                        colour="#666666"
                                        fontWeight={400}
                                    >
                                        Select currency
                                    </BaseOption>
                                    {currencies?.map((currency: Record<any, any>, index: string) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={currency.id}
                                                colour="#666666"
                                                fontWeight={400}
                                            >
                                                {currency.title}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                        {(formDetails.currency.trim()?.length > 0) && (
                            <Grid2
                                size={{ mobile: 12, laptop: 6 }}
                            >
                                <BaseFieldSet>
                                    <BaseLabel
                                        colour="#333333"
                                        fontWeight={500}
                                    >
                                        Supporting Currencies
                                    </BaseLabel>
                                    <BaseSelect
                                        name="supportingCurrencies"
                                        colour="#666666"
                                        bgcolor="#FFFFFF"
                                        fontWeight={400}
                                        multiple
                                        displayEmpty
                                        renderValue={(selected) => {
                                            if ((selected as string[])?.length === 0) {
                                                return <p style={{ marginBlock: 0 }}>Select multiple currencies</p>;
                                            }
                                            return (selected as string[])
                                                .map(id => currencies.find((c: Record<string, any>) => c.id === id)?.title || id)
                                                .join(', ');
                                        }}
                                        value={formDetails.supportingCurrencies}
                                        onChange={(e) => handleChange(e)}
                                    >
                                        <BaseOption
                                            value=""
                                            disabled
                                            colour="#666666"
                                            fontWeight={400}
                                        >
                                            Select currency
                                        </BaseOption>
                                        {currencies?.filter((currency: Record<string, any>) => currency.id !== formDetails.currency)?.map((currency: Record<any, any>, index: string) => (
                                            <BaseOption
                                                key={index}
                                                value={currency.id}
                                                colour="#666666"
                                                fontWeight={400}
                                            >
                                                {currency.title}
                                            </BaseOption>
                                        ))}
                                    </BaseSelect>
                                </BaseFieldSet>
                            </Grid2>
                        )}
                        <Grid2
                            size={{ mobile: 12, laptop: 6 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="#333333"
                                    fontWeight={500}
                                >
                                    Status
                                </BaseLabel>
                                <BaseSelect
                                    required
                                    name="status"
                                    colour="#666666"
                                    bgcolor="#FFFFFF"
                                    fontWeight={400}
                                    // IconComponent={<KeyboardArrowDown />}
                                    value={formDetails.status}
                                    onChange={(e) => handleChange(e)}
                                >
                                    <BaseOption
                                        value={" "}
                                        colour="#666666"
                                        fontWeight={400}
                                    >
                                        Select status
                                    </BaseOption>
                                    {statuses?.map((status: Record<any, any>, index: string) => {
                                        return (
                                            <BaseOption
                                                key={index}
                                                value={status.id}
                                                colour="#666666"
                                                fontWeight={400}
                                            >
                                                {status.title.charAt(0).toUpperCase() + status.title.slice(1).toLowerCase()}
                                            </BaseOption>
                                        )
                                    })}
                                </BaseSelect>
                            </BaseFieldSet>
                        </Grid2>
                    </Grid2>
                    <BaseFieldSet>
                        <BaseLabel
                            colour="#333333"
                            fontWeight={500}
                        >
                            Attach a logo (optional)
                        </BaseLabel>
                        <Box
                            component={"div"}
                            className="file-box"
                        >
                            <BaseInput
                                as={"input"}
                                type="file"
                                name="logo"
                                colour="#666666"
                                fontWeight={400}
                                inputProps={{ accept: "image/jpeg, image/png" }}
                                onChange={(e) => handleFileChange(e)}
                            />
                        </Box>
                    </BaseFieldSet>
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={16}
                        lineHeight={"normal"}
                        color={"#FF0000"}
                        whiteSpace={"normal"}
                        marginBlockStart={"var(--basic-margin)"}
                    >
                        {error}
                    </Typography>}
                    <Box
                        component={"div"}
                        className="button-box"
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            radius="7px"
                            fontSize={16}
                            fontWeight={800}
                            bgcolor="#791377"
                            padding="calc(var(--basic-padding)/3) calc(var(--basic-padding) * 1.5)"
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Save
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </CreateInstitutionWrapper>
        </Layout >
    )
}